import { useEffect, useState } from 'react';

import type { ImageProps } from 'components/common/Image';
import { loadImage } from 'components/common/Image';

export const FIRST_HOP_MS = 1000;
export const DWELL_MS = 2000;

interface UseImageRotationOptions {
  images: ImageProps[];
  isActive: boolean;
}

/**
 * Returns the index of the angle to display, cycling through `images` while
 * `isActive`. Each hop preloads the next angle and only advances once it's ready
 * (flicker-free); resets to MAIN on inactive or `images` change, skips broken angles.
 * Ported from Shopbop: https://code.amazon.com/packages/ShopbopSharedReactComponents/blobs/mainline/--/packages/shared-react-components/src/Molecules/ProductTile/useImageRotation.ts
 */
export const useImageRotation = ({ images, isActive }: UseImageRotationOptions): number => {
  const [activeIndex, setActiveIndex] = useState(0);
  const total = images.length;

  // Snap back to MAIN whenever the image set changes (e.g. a swatch swap).
  useEffect(() => {
    setActiveIndex(0);
  }, [images]);

  useEffect(() => {
    if (!isActive || total <= 1) {
      setActiveIndex(0);
      return undefined;
    }

    let cancelled = false;
    let isFirstHop = true;

    // Preload the angle at `index` and wait out the dwell before it may show.
    const preloadNextImage = (index: number) => {
      const nextImage = images[index];
      if (!nextImage) {
        return Promise.resolve();
      }
      return Promise.all([
        loadImage(nextImage),
        new Promise<void>(resolve => {
          setTimeout(resolve, isFirstHop ? FIRST_HOP_MS : DWELL_MS);
          isFirstHop = false;
        })
      ]);
    };

    // Recursively advance: only queue the next hop after the current resolves.
    const rotateFrom = (fromIndex: number) => {
      if (images.length <= 1) {
        return;
      }
      const next = (fromIndex + 1) % images.length;
      if (!images[next]) {
        return;
      }
      preloadNextImage(next).then(
        () => {
          if (cancelled) {
            return;
          }
          setActiveIndex(next);
          rotateFrom(next);
        },
        () => {
          // Broken angle: skip it and keep going.
          if (cancelled) {
            return;
          }
          rotateFrom(next);
        }
      );
    };

    rotateFrom(0);
    return () => {
      cancelled = true;
    };
  }, [isActive, total, images]);

  return activeIndex;
};
